import type { HomeQueueItem, HomeQueueResponse } from '../data/endpoints'
import type { ResourceState } from '../data/resource'
import { AsyncPanel } from '../ui/AsyncPanel'

/**
 * The home screen: what needs me right now, ranked, with counts.
 *
 * What this replaces was a heading reading "Pick a station" over a nav bar - the
 * exact anti-pattern client-and-home-screen-design was written to kill. A menu
 * puts the burden of deciding what to do next on the person least equipped to
 * decide it, and for Mitali specifically, "a menu of eight stations looks like a
 * system to learn; a single card saying Catalogue 12 books looks like a job to
 * finish."
 *
 * So: one prominent next job, the rest beneath it, counts on everything, and
 * nothing at all when there is nothing waiting. The ranking is not decided here -
 * `buildHomeQueue` in the domain decides it, server-side, from the role on the
 * session, which is why Mary cannot get Mitali's queue by changing a URL.
 *
 * The all-clear is a success state with its own words. For someone anxious about
 * being overwhelmed, seeing zero is the reward, and a blank page is not the same
 * thing as being told you are finished.
 */
export interface HomeQueueProps {
  state: ResourceState<HomeQueueResponse>
  onRetry: () => void
  go: (to: string) => void
  /** Greeting name only - never a customer name, on any role's home screen. */
  person: string
}

export function HomeQueue({ state, onRetry, go, person }: HomeQueueProps) {
  return (
    <main className="shell home">
      <header className="home-head">
        <h1 className="t-display">Hello {person}</h1>
      </header>

      <AsyncPanel state={state} emptyMessage="Nothing waiting - all clear" onRetry={onRetry}>
        {(queue) =>
          queue.allClear ? (
            <AllClear />
          ) : (
            <>
              {queue.headline ? <Headline item={queue.headline} go={go} /> : null}
              <ul className="home-list">
                {queue.items.slice(1).map((item) => (
                  <li key={item.id}>
                    <JobRow item={item} go={go} />
                  </li>
                ))}
              </ul>
              {/* Rule 4: beyond five it is a backlog, and a backlog is
                  discouraging. The rest are still reachable, behind one tap. */}
              {queue.overflow.length > 0 ? (
                <details className="home-more">
                  <summary>{queue.overflow.length} more</summary>
                  <ul className="home-list">
                    {queue.overflow.map((item) => (
                      <li key={item.id}>
                        <JobRow item={item} go={go} />
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </>
          )
        }
      </AsyncPanel>
    </main>
  )
}

/**
 * The all-clear.
 *
 * `role="status"` rather than silence, because for Mary this is also the moment
 * she is allowed to stop, and for Mitali it is the whole promise of the system
 * arriving on screen.
 */
function AllClear() {
  return (
    <p className="home-clear" role="status">
      Nothing waiting - all clear
    </p>
  )
}

/**
 * The one prominent next job. Carries the single attention pulse in the app, per
 * the one-pulse rule - and carries it only because the server marked this item,
 * so two cards can never both pulse.
 */
function Headline({ item, go }: { item: HomeQueueItem; go: (to: string) => void }) {
  return (
    <button
      type="button"
      className={`home-headline${item.attention ? ' attention' : ''}`}
      onClick={() => go(item.path)}
    >
      <span className="home-headline-verb">{item.verb}</span>
      <span className="home-headline-count">{item.count}</span>
      <span className="home-headline-noun">{item.noun}</span>
    </button>
  )
}

function JobRow({ item, go }: { item: HomeQueueItem; go: (to: string) => void }) {
  return (
    <button type="button" className="home-row" onClick={() => go(item.path)}>
      <span className="home-row-count">{item.count}</span>
      <span className="home-row-noun">{item.noun}</span>
      <span className="home-row-verb">{item.verb}</span>
    </button>
  )
}
